import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Checkbox,
} from '@chakra-ui/react'
import { LuFlaskConical, LuUserCheck, LuEyeOff, LuFileCheck } from 'react-icons/lu'
import { useEffect, useState } from 'react'
import { useUiPreferences, useUpdateUiPreferences } from '../../api/api_hooks/user/uiPreferences'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'

const STORAGE_KEY = 'careflai.aiActAcknowledged.v1'

/**
 * Reads / writes the local-storage flag that controls whether the AI Act
 * notice has been acknowledged on this device. Exposed separately so callers
 * (e.g. the tour components) can decide when to display the modal.
 *
 * NB: this is the *local cache* only — the canonical "I've seen the
 * notice" state now lives on the BE under `user.uiPreferences
 * .aiActAcknowledgedAt`. The component below reads both and treats either
 * as "acknowledged", so the dismissal sticks across devices.
 */
export function hasAcknowledgedAIAct(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

interface AIActNoticeProps {
  /** Force open the modal regardless of the local-storage flag (used from a settings page). */
  forceOpen?: boolean
  /** Called once the user clicks "J'ai compris" — also fired when forceOpen toggles off. */
  onAcknowledge?: () => void
}

/**
 * Information notice required by:
 *   - AI Act (UE 2024/1689) — high-risk medical AI system: explainability,
 *     human supervision, transparency to the end user.
 *   - RGPD Art. 13 — identity of recipients (admin support) + purpose.
 *   - Code de la santé publique L. 4001-3 — patient information on AI use.
 *
 * Persists the acknowledgement in localStorage so it only appears once
 * per device until the user clears it (or the version bumps).
 */
export default function AIActNotice({ forceOpen = false, onAcknowledge }: AIActNoticeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const { data: prefs, isLoading: prefsLoading } = useUiPreferences()
  const { mutateAsync: updatePrefs } = useUpdateUiPreferences()
  const acknowledgedOnServer = !!prefs?.aiActAcknowledgedAt

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true)
      return
    }
    // Wait for server prefs to load — they are authoritative. If the
    // server says this user hasn't acked, always show, even if the
    // browser's localStorage still carries a flag from a previous user
    // (per-browser cache, not per-user).
    if (prefsLoading) return
    if (acknowledgedOnServer) return

    // Defer one tick to avoid flashing on app boot.
    const t = window.setTimeout(() => setIsOpen(true), 250)
    return () => window.clearTimeout(t)
  }, [forceOpen, prefsLoading, acknowledgedOnServer])

  const close = async () => {
    // Write to both layers: localStorage stays as the fast-path cache so
    // the modal doesn't briefly reappear on the next mount before the
    // server prefs query resolves; the server copy is what makes the
    // dismissal stick across devices and browser-data wipes.
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
    setIsOpen(false)
    onAcknowledge?.()
    try {
      await updatePrefs({ aiActAcknowledgedAt: new Date().toISOString() })
    } catch {
      // Best-effort — localStorage already absorbed the click so the UX
      // is fine even when the network is flaky.
    }
  }

  const Bullet = ({ icon, title, body }: { icon: any; title: string; body: string }) => (
    <HStack align="start" spacing={3}>
      <Box w="36px" h="36px" borderRadius="lg" bg="purple.50" display="grid" sx={{ placeItems: 'center' }} flexShrink={0}>
        <Icon as={icon} boxSize={4} color="purple.600" />
      </Box>
      <VStack align="start" spacing={0.5}>
        <Text fontSize="sm" fontWeight="700" color="gray.800">{title}</Text>
        <Text fontSize="xs" color="gray.600" lineHeight="1.5">{body}</Text>
      </VStack>
    </HStack>
  )

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered size="3xl" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.700" />
      <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
        <ModalHeader
          bgGradient={CHROME_HEADER_GRADIENT}
          color="white"
          py={5}
          px={7}
        >
          <HStack spacing={4}>
            <Box w="46px" h="46px" borderRadius="xl" bg="whiteAlpha.200" display="grid" sx={{ placeItems: 'center' }}>
              <Icon as={LuFlaskConical} boxSize={6} />
            </Box>
            <VStack align="start" spacing={0.5}>
              <HStack spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="tight">
                  Careflai — plateforme expérimentale
                </Text>
                <Box
                  fontSize="2xs"
                  fontWeight="700"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  bg="whiteAlpha.300"
                  border="1px solid"
                  borderColor="whiteAlpha.500"
                  color="white"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                >
                  Bêta
                </Box>
              </HStack>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">
                Aide à l'analyse d'images biologiques · Usage & confidentialité
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalBody py={6} px={7}>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.700" lineHeight="1.65">
              Careflai est une <strong>plateforme expérimentale</strong> d'aide à l'analyse
              d'images biologiques (Western Blot, paludisme…). Elle a été conçue pour vous
              faire gagner du temps sur les tâches répétitives, tout en gardant votre
              expertise au cœur de chaque décision. Voici quelques repères avant de commencer.
            </Text>

            <Bullet
              icon={LuUserCheck}
              title="Aide à l'analyse — pas un diagnostic"
              body="Les propositions produites par les modèles sont indicatives et doivent être vérifiées. La décision clinique reste sous la responsabilité du professionnel de santé qui l'exporte."
            />
            <Bullet
              icon={LuEyeOff}
              title="Accès en support technique"
              body="L'équipe Careflai peut accéder en lecture à vos cas pour le support, le débogage et l'amélioration des modèles. Chaque accès est tracé — consultez le journal sous Mon profil → Confidentialité."
            />
            <Bullet
              icon={LuFileCheck}
              title="Données patient — votre responsabilité"
              body="Sauf accord HDS explicite, n'uploadez que des données pseudonymisées (pas de nom, prénom ou NIR identifiable). Les images et métadonnées sont stockées chez Careflai et restent exportables ou supprimables à tout moment sur demande."
            />

            <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={3.5}>
              <Text fontSize="xs" color="purple.900" lineHeight="1.6">
                <strong>Vous participez à un programme pilote.</strong> Careflai est
                actuellement déployée en avant-première auprès de CHU partenaires. Vos
                retours contribuent directement à l'amélioration continue des modèles.
                Notre développement intègre dès aujourd'hui les principes de supervision
                humaine, de traçabilité et de transparence.
              </Text>
            </Box>

            <Checkbox
              isChecked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              colorScheme="purple"
              size="sm"
              alignItems="flex-start"
              spacing={3}
              mt={1}
            >
              <Text fontSize="xs" color="gray.700" lineHeight="1.55">
                J'ai pris connaissance du caractère expérimental de la plateforme et
                je m'engage à valider chaque résultat par mon expertise avant toute
                conclusion clinique.
              </Text>
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" py={4} px={7}>
          <Button
            colorScheme="purple"
            onClick={close}
            isDisabled={!confirmed}
            size="md"
            borderRadius="lg"
            fontWeight="600"
          >
            J'ai compris — accéder à la plateforme
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
